/**
 * bootFromSupabase — fetches profile + tables and hydrates the state
 * signal once a session is established.
 *
 * Every DB read here goes through the raw-fetch helpers
 * (`restGet` / `restGetAll`) rather than the Supabase JS SDK. The SDK's
 * auth helpers acquire the `navigator.locks` "sb-<ref>-auth-token"
 * resource that the realtime channel also holds; under any concurrency
 * the SDK's internal getSession() can throw "Lock … was released
 * because another request stole it", which crashes boot entirely and
 * leaves the user with no sync baseline — pushes silently fail forever
 * after that. We hit this for real with daniele.difini@icons.it on
 * 2026-05-25. The raw-fetch path reads the JWT directly from
 * localStorage and never touches the SDK lock.
 *
 * Same hardening pattern as the calendar save pipeline (`restUpsert`),
 * admin promotion (`restPatch`), sign-out (custom logout in
 * `supabase.ts`), and the password-set modal (`restUpdatePassword`).
 */
import type { Session } from "@supabase/supabase-js";
import { signOut } from "../supabase";
import { state, patchState } from "../state/store";
import { recordSyncBaseline } from "../state/storage";
import { subscribeRealtime } from "../state/realtime";
import { toast } from "../utils/toast";
import { activePeople } from "../utils/names";
import { restGet, restGetAll } from "../utils/withRetry";
import {
  ADMIN_NAME,
  type CalendarMap,
  type NoteMap,
  type MeetingMap,
  type ProfilesByRoster,
  type ProfileRow,
  type RosterMap,
  type StatusCode,
} from "../types";

interface RosterRowShape {
  name: string;
  unit: string | null;
  role: string | null;
  email: string | null;
  active: boolean | null;
  first_name: string | null;
  last_name: string | null;
  last_active_day: string | null;
}

export async function bootFromSupabase(session: Session): Promise<void> {
  try {
    // 1. Profile (auto-created by handle_new_user trigger on first sign-in).
    //    restGet returns an array; for a single-id lookup we expect 0 or 1.
    const profiles = await restGet<ProfileRow>(
      "profiles",
      `id=eq.${encodeURIComponent(session.user.id)}` +
        "&select=id,email,roster_name,is_admin,is_super_admin,password_set",
    );
    if (profiles.length === 0) {
      throw new Error(
        "Your profile wasn't found — the auto-create trigger may not have fired. Refresh the page or contact the admin.",
      );
    }
    const profile = profiles[0]!;

    // 2. Roster (everyone authenticated can read; admin-only writes).
    //    ~150 rows — well under PostgREST's 1000-row cap, so a single
    //    restGet is enough.
    const rosterRows = await restGet<RosterRowShape>(
      "roster",
      "select=name,unit,role,email,active,first_name,last_name,last_active_day",
    );

    const rosterMap: RosterMap = {};
    rosterRows.forEach((r) => {
      rosterMap[r.name] = {
        unit: r.unit ?? "",
        role: r.role ?? "",
        email: r.email ?? "",
        active: r.active !== false,
        first_name: r.first_name ?? "",
        last_name: r.last_name ?? "",
        last_active_day: r.last_active_day ?? null,
      };
    });

    // 3. Calendar / notes / meetings — paginated. Calendar is ~44k rows
    //    today, growing ~5k/month. Closures is tiny (handful of rows).
    //    All four fired in parallel so the cold-boot latency is bounded
    //    by the slowest of them rather than their sum.
    const [calRows, noteRows, mtgRows, closureRows] = await Promise.all([
      restGetAll<{ person: string; day: string; status: StatusCode }>(
        "calendar",
        "person,day,status",
      ),
      restGetAll<{ person: string; day: string; text: string }>(
        "notes",
        "person,day,text",
      ),
      restGetAll<{ person: string; day: string; description: string }>(
        "meetings",
        "person,day,description",
      ),
      restGetAll<{ day: string; label: string }>("closures", "day,label"),
    ]);

    const calMap: CalendarMap = {};
    const noteMap: NoteMap = {};
    const mtgMap: MeetingMap = {};
    calRows.forEach((row) => {
      (calMap[row.person] = calMap[row.person] ?? {})[row.day] = row.status;
    });
    noteRows.forEach((row) => {
      (noteMap[row.person] = noteMap[row.person] ?? {})[row.day] = String(row.text ?? "");
    });
    mtgRows.forEach((row) => {
      (mtgMap[row.person] = mtgMap[row.person] ?? {})[row.day] = row.description ?? "";
    });

    const closureMap: Record<string, string> = {};
    closureRows.forEach((row) => {
      closureMap[row.day] = row.label ?? "";
    });

    // 4. Admin-only: pull every other profile so the Directory's Access
    //    column has data to render. RLS lets admins read all profiles.
    const profilesByRoster: ProfilesByRoster = {};
    if (profile.is_admin) {
      const allProfiles = await restGet<ProfileRow>(
        "profiles",
        "select=id,email,roster_name,is_admin,is_super_admin,password_set",
      );
      allProfiles.forEach((p) => {
        if (p.roster_name) profilesByRoster[p.roster_name] = p;
      });
    }

    // 5. Identity setup
    let currentUser: string | null;
    let actAs: string | null = state.value.actAs;
    if (profile.is_admin) {
      currentUser = ADMIN_NAME;
      if (profile.is_super_admin) {
        // Super admins are the only tier that can edit any teammate's
        // calendar. Pick a sensible default if their previous choice
        // is gone (or never set).
        const names = activePeople(rosterMap);
        if (!actAs || !rosterMap[actAs]) {
          actAs = names[0] ?? null;
        }
      } else {
        // Plain admins keep their admin powers for the Directory and
        // Dashboard, but are locked to their OWN calendar for editing.
        // Force actAs to their own roster row regardless of any prior
        // value left in state — that could be stale from before the
        // restriction was introduced, or set in dev tools. Setting it
        // here means the existing `editingTarget` computed
        // (isAdmin ? actAs : currentUser) still works without changes,
        // and the UI's act-as picker (now gated on isSuperAdmin) won't
        // be available to override it.
        // If their email doesn't match a roster row, actAs stays null
        // and MyCalendar renders an empty-state message.
        actAs = profile.roster_name ?? null;
      }
    } else {
      currentUser = profile.roster_name;
      actAs = null;
      if (!currentUser) {
        toast("Your email isn't on the roster. Contact the admin.");
        await signOut();
        return;
      }
      // Server-side belt: handle_new_user (migration 13) rejects new
      // sign-ups for inactive roster rows. Client-side suspenders for
      // the case where someone already has a profile and their roster
      // row was flipped inactive between sessions — Supabase doesn't
      // re-run handle_new_user on subsequent sign-ins, so without
      // this check a deactivated former employee would still get into
      // the app every time they signed in with their existing token.
      const myRosterRow = rosterMap[currentUser];
      if (myRosterRow && myRosterRow.active === false) {
        toast("Your account has been deactivated. Contact the admin.");
        await signOut();
        return;
      }
    }

    // 6. Replace state in one go (single signal write → single render)
    patchState({
      roster: rosterMap,
      calendar: calMap,
      notes: noteMap,
      meetings: mtgMap,
      isAdmin: profile.is_admin,
      // Super admin is a strict superset of admin — DB invariant enforced
      // by migration 10's trigger. Coerce to false if the column is
      // missing (older DBs / pre-migration sessions).
      isSuperAdmin: !!profile.is_super_admin,
      currentUser,
      actAs,
      profileId: profile.id,
      adminEmail: session.user.email ?? profile.email ?? "",
      profilesByRoster,
      closures: closureMap,
    });

    // 7. Sync baseline + realtime + cache. Order matters:
    //    - baseline first (otherwise the next mutation tries to push
    //      everything we just loaded as a "new" diff)
    //    - realtime second (so changes during the rest of boot are queued)
    recordSyncBaseline();
    subscribeRealtime();

    return;
  } catch (err) {
    console.error("bootFromSupabase failed:", err);
    toast("Could not load your data — please reload.");
    throw err;
  }
}

/** Returns true if the modal should fire for first-time setup. */
export async function shouldPromptSetPassword(profileId: string): Promise<boolean> {
  // Re-read from server in case the flag was flipped between sessions.
  // Uses restGet to avoid the SDK auth-lock contention that the rest
  // of the boot path was crashing on (see top-of-file comment).
  try {
    const rows = await restGet<{ password_set: boolean }>(
      "profiles",
      `id=eq.${encodeURIComponent(profileId)}&select=password_set`,
    );
    if (rows.length === 0) return false;
    return !rows[0]!.password_set;
  } catch {
    return false;
  }
}
